import React, { forwardRef, useImperativeHandle } from "react";

import { isEmail, minLength, maxLength } from "utility/FormUtils";
import { useNotificationManager } from "notifications/NotificationManager";

interface InputProps {
    className?: string;
    name: string;
    noLabel?: boolean;
    overrideLabel?: string;
    overrideText?: string;
    readonly?: boolean;
    required?: boolean;
    type: string;
    validations?: Array<{ type: string; value: number | string }>;
    validateOnBlur?: boolean;
    value: string;
    // Callbacks
    onChange: (value: string) => void;
}

export interface InputRef {
    validate: () => boolean;
}

const Input = forwardRef<InputRef, InputProps>(({
    className,
    name,
    noLabel,
    overrideText,
    readonly,
    required,
    type,
    validations,
    validateOnBlur = false,
    value,
    // Callbacks
    onChange
}, ref) => {
    const notificationManager = useNotificationManager();

    // ---=== Utilities ===--- //

    const validate = (): boolean => {
        let isValid = true;

        value = value.trim();
        if (value && validations) {
            validations.forEach((validation) => {
                switch (validation.type) {
                    case "email":
                        if (!isEmail(value)) {
                            notificationManager.addError("Not a valid email address");
                            isValid = false;
                        }
                        break;
                    case "minLength":
                        if (!minLength(value, validation.value as number)) {
                            notificationManager.addError(`${name} must be at least ${validation.value} characters`);
                            isValid = false;
                        }
                        break;
                    case "maxLength":
                        if (!maxLength(value, validation.value as number)) {
                            notificationManager.addError(`${name} cannot be more ${validation.value} characters`);
                            isValid = false;
                        }
                        break;
                }
            });
        }

        if (required && (!value || value.length === 0)) {
            notificationManager.addError(`${name} is required`);
            isValid = false;
        }

        return isValid;
    };

    // Expose validate method to parent components
    useImperativeHandle(ref, () => ({
        validate
    }));

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        onChange(event.target.value.trim());
    };

    const onBlur: React.FocusEventHandler = (): void => {
        if (validateOnBlur) {
            notificationManager.clearAllNotifications();
            validate();
        }
    };

    // ---=== Render ===--- //

    return (
        <React.Fragment>
            <input
                className={className}
                name={name}
                onBlur={onBlur}
                onChange={handleChange}
                onKeyUp={
                    (e: React.KeyboardEvent): void => {
                        e.key === "Enter" && e.preventDefault();
                    }
                }
                placeholder={overrideText ? overrideText : !noLabel ? `Enter ${name.toLowerCase()}` : name}
                readOnly={readonly}
                type={type}
                value={value.trim()}
            />
        </React.Fragment>
    );
});

Input.displayName = "Input";

export default Input;